/**
 * Pure recurrence utilities for assessment scheduling.
 *
 * All date arithmetic uses local calendar dates to avoid UTC day-shift bugs.
 * The `calculateNextAssessmentSendDate` function is deterministic and side-effect-free.
 *
 * The Recurring Assessment cron bot creates occurrence Tasks when each standing order's
 * next-send date is due.
 */

import { formatDateOnlyLocal, parseDateOnlyLocal } from './dateHelpers';

export const ASSESSMENT_FREQUENCIES = [
  'Once',
  'Every Session',
  'Weekly',
  'Biweekly',
  'Monthly',
  'Bimonthly',
  'Quarterly',
] as const;

export type AssessmentFrequency = (typeof ASSESSMENT_FREQUENCIES)[number];

/**
 * Compute the next send date for a recurring assessment.
 *
 * @param currentSendDate - YYYY-MM-DD of the current/most-recent send.
 * @param frequency       - One of the ASSESSMENT_FREQUENCIES values.
 * @param endDate         - Optional YYYY-MM-DD; if next exceeds this, returns null.
 * @returns YYYY-MM-DD string for the next send, or null when no next send should occur.
 */
export function calculateNextAssessmentSendDate(
  currentSendDate: string,
  frequency: AssessmentFrequency,
  endDate?: string,
): string | null {
  if (frequency === 'Once' || frequency === 'Every Session') return null;

  const current = parseDateOnlyLocal(currentSendDate);
  if (!current || Number.isNaN(current.getTime())) return null;

  let next: Date;

  switch (frequency) {
    case 'Weekly':
      next = addDays(current, 7);
      break;
    case 'Biweekly':
      next = addDays(current, 14);
      break;
    case 'Monthly':
      next = addMonths(current, 1);
      break;
    case 'Bimonthly':
      next = addMonths(current, 2);
      break;
    case 'Quarterly':
      next = addMonths(current, 3);
      break;
    default:
      return null;
  }

  const nextStr = formatDateOnlyLocal(next);

  if (endDate) {
    const end = parseDateOnlyLocal(endDate);
    if (end && !Number.isNaN(end.getTime()) && next.getTime() > end.getTime()) {
      return null;
    }
  }

  return nextStr;
}

function addDays(d: Date, days: number): Date {
  const result = new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
  return result;
}

/**
 * Add calendar months, clamping to the last day of the target month.
 * e.g. Jan 31 + 1 month = Feb 28 (or 29).
 */
function addMonths(d: Date, months: number): Date {
  const targetMonth = d.getMonth() + months;
  const targetDay = d.getDate();
  const result = new Date(d.getFullYear(), targetMonth, 1);
  const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(targetDay, lastDayOfTargetMonth));
  return result;
}

/** True if the frequency value drives recurring sends (not Once, not Every Session). */
export function isRecurringFrequency(frequency: string): boolean {
  return (
    frequency !== 'Once' &&
    frequency !== 'Every Session' &&
    ASSESSMENT_FREQUENCIES.includes(frequency as AssessmentFrequency)
  );
}
